import type { WorkbenchSaveWarning } from "@credit-acceptance/step6-bff-client";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { t } from "i18next";

import { dscWorkbenchApi } from "../../../../../api/client";
import { useForceExitStore } from "../../../../../stores/forceExitStore";
import { stipulationKeys } from "../../../../stipulations/api/stipulationKeys";
import { dscWorkbenchKeys } from "../../../api/dscWorkbenchKeys";
import { buildSignerApplicantPutRequest } from "../Signers.putMapping";
import type { PersonInput } from "../Signers.schema";

export interface SaveSignerApplicantInput {
  approvalId: string;
  driver: PersonInput;
  secondSigner?: PersonInput;
}

export interface SaveSignerApplicantResult {
  warnings: WorkbenchSaveWarning[];
}

/**
 * Falls back to a timestamp+random key when crypto.randomUUID is unavailable
 * (older browsers, non-secure contexts) so double-submit dedup never silently
 * goes missing.
 * @returns {string} A unique key suitable for the Idempotency-Key header.
 */
function generateIdempotencyKey(): string {
  return globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

/**
 * Mutation hook for saving the Signers card (Income, Bank, Customer Info) to
 * the BFF. Backed exclusively by WorkbenchDashboardApi.save (POST
 * /api/v1/workbench/applications/{approvalId}) — the single aggregate save
 * endpoint for the whole Workbench. A per-attempt `Idempotency-Key` dedups
 * accidental double-submits. On success, both the dashboard and the
 * stipulations list are invalidated: the save can trigger a post-commit
 * stipulation recalc (see `stipulationActionsResult` on the response), so open
 * stips may change as a result of this save.
 * @returns {ReturnType<typeof useMutation>} The TanStack Mutation result.
 */
export function useSaveSignerApplicant() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      approvalId,
      driver,
      secondSigner,
    }: SaveSignerApplicantInput): Promise<SaveSignerApplicantResult> => {
      // Read live store state (not a subscribed hook value) so a force-exit
      // flag set moments earlier by ReviewPage's click-time recheck is seen
      // even though this component hasn't re-rendered yet.
      if (useForceExitStore.getState().shouldForceExit) {
        throw new Error(t("signers.saveAbortedForceExit"));
      }

      const idempotencyKey = generateIdempotencyKey();
      const response = await dscWorkbenchApi.save(
        approvalId,
        {
          signerApplicant: buildSignerApplicantPutRequest(driver, false),
          secondSignerApplicant: secondSigner ? buildSignerApplicantPutRequest(secondSigner, true) : undefined,
        },
        idempotencyKey
      );

      return { warnings: response.data.metadata?.warnings ?? [] };
    },
    onSuccess: (_result, { approvalId }) => {
      void queryClient.invalidateQueries({ queryKey: dscWorkbenchKeys.detail({ approvalId }) });
      void queryClient.invalidateQueries({ queryKey: stipulationKeys.lists() });
    },
  });
}
